import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  CheckCircle2,
  ChevronRight,
  Search,
  Award,
  Target,
  Footprints,
  Link2,
  ClipboardList,
  Sparkles,
  UserCheck,
  ShieldCheck,
  X,
  Check,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrilhoReinoPage() {
  const navigate = useNavigate();
  const { token, filialId } = useAuth();
  const [etapas, setEtapas] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEtapaModal, setShowEtapaModal] = useState(false);
  const [editingEtapa, setEditingEtapa] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEtapa, setFilterEtapa] = useState("all");
  const [etapaForm, setEtapaForm] = useState({
    nome: "",
    descricao: "",
    ordem: 0,
    ativo: true,
    tipo_etapa: "",  // "", "aprovacao_membro", "aprovacao_voluntario"
    vinculo_automatico: "",  // Campo do sistema que marca automaticamente
    vinculo_valor: "",        // Valor que ativa a marcação automática
    vinculo_projeto_id: "",   // Projeto que marca automaticamente quando aprovado (deprecated)
    vinculo_projeto_status: "aprovado"  // Status que ativa (deprecated)
  });
  
  const [projects, setProjects] = useState([]);
  
  // States for progress modal
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [pessoaProgresso, setPessoaProgresso] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);

  // Tipos especiais de etapa
  const tiposEtapa = [
    { id: "", label: "Etapa Normal", descricao: "Etapa manual sem ação automática" },
    { id: "aprovacao_membro", label: "Aprovação de Membro", descricao: "Ao marcar, pessoa será aprovada como membro da igreja" },
    { id: "aprovacao_voluntario", label: "Aprovação de Voluntário", descricao: "Ao marcar, pessoa ficará disponível para escala (ou entrará em audição se o ministério exigir)" },
    { id: "mentor", label: "Pode ser conexão (Igreja Conectada)", descricao: "Quem tiver esta etapa concluída poderá ser a conexão de outras pessoas" },
    { id: "conexao", label: "Conectado (Igreja Conectada)", descricao: "Marcada automaticamente quando a pessoa recebe uma conexão" }
  ];

  // Opções de campos do sistema que podem marcar etapas automaticamente
  const camposVinculo = [
    { id: "", label: "Nenhum (manual)" },
    { id: "situacao", label: "Situação do Cadastro", valores: [
      "Sou Membro da Life",
      "Aceitei Jesus",
      "Reconciliei",
      "Sou cristão e quero me unir à Life",
      "Sou cristão mas ainda não sou parte da Life",
      "Frequento eventualmente"
    ]},
    { id: "batismo", label: "Batismo", valores: [
      "Já sou batizado",
      "Desejo me batizar",
      "Nenhuma das opções"
    ]}
  ];
  
  const statusOptions = [
    { id: "aprovado", label: "Aprovado" },
    { id: "concluido", label: "Concluído" },
    { id: "inscrito", label: "Inscrito" }
  ];

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, [filialId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [etapasRes, pessoasRes, projectsRes] = await Promise.all([
        axios.get(`${API}/trilho/etapas`, { headers, params: { filial_id: filialId } }),
        axios.get(`${API}/trilho/pessoas`, { headers, params: { filial_id: filialId } }),
        axios.get(`${API}/projects`, { headers }).catch(() => ({ data: [] }))
      ]);
      setEtapas(etapasRes.data || []);
      setPessoas(pessoasRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEtapa = async () => {
    if (!etapaForm.nome.trim()) {
      toast.error("Nome da etapa é obrigatório");
      return;
    }

    try {
      const data = {
        ...etapaForm,
        filial_id: filialId,
        ordem: etapaForm.ordem || etapas.length
      };

      if (editingEtapa) {
        await axios.put(`${API}/trilho/etapas/${editingEtapa.id}`, data, { headers });
        toast.success("Etapa atualizada com sucesso");
      } else {
        await axios.post(`${API}/trilho/etapas`, data, { headers });
        toast.success("Etapa criada com sucesso");
      }

      setShowEtapaModal(false);
      setEditingEtapa(null);
      setEtapaForm({ nome: "", descricao: "", ordem: 0, ativo: true, tipo_etapa: "", vinculo_automatico: "", vinculo_valor: "", vinculo_projeto_id: "", vinculo_projeto_status: "aprovado" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar etapa");
    }
  };

  const handleDeleteEtapa = async (etapa) => {
    if (!window.confirm(`Deseja excluir a etapa "${etapa.nome}"?`)) return;

    try {
      await axios.delete(`${API}/trilho/etapas/${etapa.id}`, { headers });
      toast.success("Etapa excluída com sucesso");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir etapa");
    }
  };

  // Open progress modal for a person
  const handleOpenProgress = async (pessoa, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedPessoa(pessoa);
    setShowProgressModal(true);
    setLoadingProgress(true);
    
    try {
      const res = await axios.get(`${API}/trilho/progresso/pessoa/${pessoa.id}`, { headers });
      setPessoaProgresso(res.data || []);
    } catch (error) {
      console.error("Error loading progress:", error);
      setPessoaProgresso([]);
    } finally {
      setLoadingProgress(false);
    }
  };

  // Check if etapa is completed for selected person
  const isEtapaCompleted = (etapaId) => {
    return pessoaProgresso.some(p => p.etapa_id === etapaId);
  };

  // Get progress record for etapa
  const getProgressRecord = (etapaId) => {
    return pessoaProgresso.find(p => p.etapa_id === etapaId);
  };

  // Add progress for an etapa
  const handleAddProgress = async (etapaId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!selectedPessoa) return;
    
    try {
      await axios.post(`${API}/trilho/progresso`, {
        pessoa_id: selectedPessoa.id,
        etapa_id: etapaId
      }, { headers });
      
      toast.success("Etapa marcada!");
      
      // Reload progress
      const res = await axios.get(`${API}/trilho/progresso/pessoa/${selectedPessoa.id}`, { headers });
      setPessoaProgresso(res.data || []);
      
      // Update local pessoas state
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao marcar etapa");
    }
  };

  // Remove progress for an etapa
  const handleRemoveProgress = async (progressId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!window.confirm("Deseja desmarcar esta etapa?")) return;
    
    try {
      await axios.delete(`${API}/trilho/progresso/${progressId}`, { headers });
      toast.success("Etapa desmarcada");
      
      // Reload progress
      const res = await axios.get(`${API}/trilho/progresso/pessoa/${selectedPessoa.id}`, { headers });
      setPessoaProgresso(res.data || []);
      
      // Update local pessoas state
      loadData();
    } catch (error) {
      toast.error("Erro ao desmarcar etapa");
    }
  };

  const handleInicializarEtapas = async () => {
    try {
      const res = await axios.post(`${API}/trilho/etapas/inicializar`, null, { 
        headers,
        params: { filial_id: filialId }
      });
      if (res.data.etapas_criadas?.length > 0) {
        toast.success(res.data.message);
        loadData();
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error("Erro ao inicializar etapas");
    }
  };

  const openEditModal = (etapa) => {
    setEditingEtapa(etapa);
    setEtapaForm({
      nome: etapa.nome,
      descricao: etapa.descricao || "",
      ordem: etapa.ordem || 0,
      ativo: etapa.ativo !== false,
      tipo_etapa: etapa.tipo_etapa || "",
      vinculo_automatico: etapa.vinculo_automatico || "",
      vinculo_valor: etapa.vinculo_valor || "",
      vinculo_projeto_id: etapa.vinculo_projeto_id || "",
      vinculo_projeto_status: etapa.vinculo_projeto_status || "aprovado"
    });
    setShowEtapaModal(true);
  };

  const openNewModal = () => {
    setEditingEtapa(null);
    setEtapaForm({
      nome: "",
      descricao: "",
      ordem: etapas.length,
      ativo: true,
      tipo_etapa: "",
      vinculo_automatico: "",
      vinculo_valor: "",
      vinculo_projeto_id: "",
      vinculo_projeto_status: "aprovado"
    });
    setShowEtapaModal(true);
  };

  const getCampoVinculo = (campoId) => camposVinculo.find(c => c.id === campoId);
  
  const getProjetoNome = (projetoId) => {
    const projeto = projects.find(p => p.id === projetoId);
    return projeto?.nome || projetoId;
  };

  const getTipoEtapaLabel = (tipoEtapa) => {
    const tipo = tiposEtapa.find(t => t.id === tipoEtapa);
    return tipo?.label || null;
  };

  const getVinculoLabel = (etapa) => {
    // Primeiro verifica tipo especial de etapa
    if (etapa.tipo_etapa) {
      const tipo = tiposEtapa.find(t => t.id === etapa.tipo_etapa);
      if (tipo) return tipo.label;
    }
    // Depois verifica vínculo com projeto (deprecated)
    if (etapa.vinculo_projeto_id) {
      const projeto = projects.find(p => p.id === etapa.vinculo_projeto_id);
      if (projeto) {
        return `Projeto: ${projeto.nome} (${etapa.vinculo_projeto_status || 'aprovado'})`;
      }
    }
    // Depois verifica vínculo com campo do sistema
    if (!etapa.vinculo_automatico) return null;
    const campo = getCampoVinculo(etapa.vinculo_automatico);
    if (!campo) return null;
    return `${campo.label}: ${etapa.vinculo_valor}`;
  };

  // Filter pessoas
  const filteredPessoas = pessoas.filter(pessoa => {
    const matchesSearch = pessoa.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEtapa = filterEtapa === "all" || pessoa.etapas_concluidas?.includes(filterEtapa);
    return matchesSearch && matchesEtapa;
  });

  // Get progress percentage for each pessoa
  const getProgressPercent = (pessoa) => {
    if (!pessoa.total_etapas || pessoa.total_etapas === 0) return 0;
    return Math.round((pessoa.etapas_concluidas_count / pessoa.total_etapas) * 100);
  };

  // Get etapa name by id
  const getEtapaNome = (etapaId) => {
    const etapa = etapas.find(e => e.id === etapaId);
    return etapa?.nome || etapaId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Footprints className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Trilho do Reino</h1>
            <p className="text-sm text-muted-foreground">Jornada de crescimento espiritual</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4 text-center">
              <Footprints className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <div className="text-2xl font-bold">{etapas.length}</div>
              <div className="text-xs opacity-80">Etapas</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <div className="text-2xl font-bold">{pessoas.length}</div>
              <div className="text-xs opacity-80">Pessoas</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4 text-center">
              <Award className="h-6 w-6 mx-auto mb-1 opacity-80" />
              <div className="text-2xl font-bold">
                {pessoas.filter(p => p.etapas_concluidas_count === p.total_etapas && p.total_etapas > 0).length}
              </div>
              <div className="text-xs opacity-80">Concluíram</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pessoas" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pessoas" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Pessoas
            </TabsTrigger>
            <TabsTrigger value="etapas" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Etapas
            </TabsTrigger>
          </TabsList>

          {/* Pessoas Tab */}
          <TabsContent value="pessoas" className="space-y-4">
            {/* Search and Filter */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar pessoa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-pessoa"
                />
              </div>
              <select
                value={filterEtapa}
                onChange={(e) => setFilterEtapa(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm bg-background"
                data-testid="filter-etapa"
              >
                <option value="all">Todas etapas</option>
                {etapas.map(e => (
                  <option key={e.id} value={e.id}>{e.nome}</option>
                ))}
              </select>
            </div>

            {/* Pessoas List */}
            <div className="space-y-3">
              {filteredPessoas.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma pessoa encontrada</p>
                  </CardContent>
                </Card>
              ) : (
                filteredPessoas.map(pessoa => (
                  <Card 
                    key={pessoa.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={(e) => handleOpenProgress(pessoa, e)}
                    data-testid={`pessoa-card-${pessoa.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{pessoa.nome_completo}</h3>
                            {pessoa.etapas_concluidas_count === pessoa.total_etapas && pessoa.total_etapas > 0 && (
                              <Badge variant="default" className="bg-green-500">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Completo
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {pessoa.etapas_concluidas_count || 0} de {pessoa.total_etapas || 0} etapas
                          </p>
                          
                          {/* Progress Bar */}
                          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                              style={{ width: `${getProgressPercent(pessoa)}%` }}
                            />
                          </div>
                          
                          {/* Etapas badges */}
                          {pessoa.etapas_concluidas?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {pessoa.etapas_concluidas.slice(0, 3).map(etapaId => (
                                <Badge key={etapaId} variant="secondary" className="text-xs">
                                  {getEtapaNome(etapaId)}
                                </Badge>
                              ))}
                              {pessoa.etapas_concluidas.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{pessoa.etapas_concluidas.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Etapas Tab */}
          <TabsContent value="etapas" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Etapas do Trilho</h3>
              <div className="flex gap-2">
                <Button onClick={handleInicializarEtapas} variant="outline" size="sm" data-testid="init-etapas-btn">
                  <Sparkles className="h-4 w-4 mr-1" />
                  Inicializar Padrão
                </Button>
                <Button onClick={openNewModal} size="sm" data-testid="add-etapa-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Nova Etapa
                </Button>
              </div>
            </div>

            {etapas.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="mb-4">Nenhuma etapa cadastrada</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={handleInicializarEtapas} variant="outline">
                      <Sparkles className="h-4 w-4 mr-1" />
                      Inicializar Padrão
                    </Button>
                    <Button onClick={openNewModal} variant="outline">
                      <Plus className="h-4 w-4 mr-1" />
                      Criar etapa
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {etapas.map((etapa, index) => (
                  <Card 
                    key={etapa.id} 
                    className={`${!etapa.ativo ? 'opacity-50' : ''}`}
                    data-testid={`etapa-card-${etapa.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold">{etapa.nome}</h4>
                            {!etapa.ativo && (
                              <Badge variant="secondary">Inativa</Badge>
                            )}
                            {etapa.tipo_etapa === "aprovacao_membro" && (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Aprova Membro
                              </Badge>
                            )}
                            {etapa.tipo_etapa === "aprovacao_voluntario" && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                <ShieldCheck className="h-3 w-3 mr-1" />
                                Aprova Voluntário
                              </Badge>
                            )}
                            {etapa.tipo_etapa === "mentor" && (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Pode ser conexão
                              </Badge>
                            )}
                            {etapa.tipo_etapa === "conexao" && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <Link2 className="h-3 w-3 mr-1" />
                                Conexão automática
                              </Badge>
                            )}
                            {etapa.vinculo_automatico && !etapa.tipo_etapa && (
                              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                                <Link2 className="h-3 w-3 mr-1" />
                                Auto
                              </Badge>
                            )}
                          </div>
                          {etapa.descricao && (
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {etapa.descricao}
                            </p>
                          )}
                          {getVinculoLabel(etapa) && !etapa.tipo_etapa && (
                            <p className="text-xs text-blue-600 mt-1">
                              {getVinculoLabel(etapa)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditModal(etapa)}
                            data-testid={`edit-etapa-${etapa.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteEtapa(etapa)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-etapa-${etapa.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Etapa Modal */}
      <Dialog open={showEtapaModal} onOpenChange={setShowEtapaModal}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingEtapa ? "Editar Etapa" : "Nova Etapa"}
            </DialogTitle>
            <DialogDescription>
              Configure os detalhes da etapa do Trilho do Reino
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Etapa *</Label>
              <Input
                id="nome"
                value={etapaForm.nome}
                onChange={(e) => setEtapaForm({ ...etapaForm, nome: e.target.value })}
                placeholder="Ex: Batismo nas Águas"
                data-testid="etapa-nome"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={etapaForm.descricao}
                onChange={(e) => setEtapaForm({ ...etapaForm, descricao: e.target.value })}
                placeholder="Descrição da etapa..."
                rows={2}
                data-testid="etapa-descricao"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ordem">Ordem</Label>
              <Input
                id="ordem"
                type="number"
                value={etapaForm.ordem}
                onChange={(e) => setEtapaForm({ ...etapaForm, ordem: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="etapa-ordem"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="ativo">Etapa Ativa</Label>
              <Switch
                id="ativo"
                checked={etapaForm.ativo}
                onCheckedChange={(checked) => setEtapaForm({ ...etapaForm, ativo: checked })}
                data-testid="etapa-ativo"
              />
            </div>

            {/* Tipo de Etapa Especial */}
            <div className="space-y-3 p-3 rounded-lg bg-purple-50 border border-purple-100">
              <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Tipo de Etapa
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="tipo_etapa" className="text-sm">Ação Especial ao Marcar</Label>
                <Select
                  value={etapaForm.tipo_etapa || "none"}
                  onValueChange={(value) => setEtapaForm({ 
                    ...etapaForm, 
                    tipo_etapa: value === "none" ? "" : value
                  })}
                >
                  <SelectTrigger data-testid="etapa-tipo">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEtapa.map(tipo => (
                      <SelectItem key={tipo.id || "none"} value={tipo.id || "none"}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {etapaForm.tipo_etapa && (
                  <p className="text-xs text-purple-600">
                    {tiposEtapa.find(t => t.id === etapaForm.tipo_etapa)?.descricao}
                  </p>
                )}
              </div>
            </div>

            {/* Vínculo Automático */}
            <div className="space-y-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm font-medium text-blue-700 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Marcar Automaticamente
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="vinculo_campo" className="text-sm">Campo do Sistema</Label>
                <Select
                  value={etapaForm.vinculo_automatico || "none"}
                  onValueChange={(value) => setEtapaForm({ 
                    ...etapaForm, 
                    vinculo_automatico: value === "none" ? "" : value,
                    vinculo_valor: ""
                  })}
                >
                  <SelectTrigger data-testid="etapa-vinculo-campo">
                    <SelectValue placeholder="Selecione um campo" />
                  </SelectTrigger>
                  <SelectContent>
                    {camposVinculo.map(campo => (
                      <SelectItem key={campo.id || "none"} value={campo.id || "none"}>
                        {campo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {etapaForm.vinculo_automatico && getCampoVinculo(etapaForm.vinculo_automatico)?.valores && (
                <div className="space-y-2">
                  <Label htmlFor="vinculo_valor" className="text-sm">Valor que Ativa</Label>
                  <Select
                    value={etapaForm.vinculo_valor || "none"}
                    onValueChange={(value) => setEtapaForm({ 
                      ...etapaForm, 
                      vinculo_valor: value === "none" ? "" : value
                    })}
                  >
                    <SelectTrigger data-testid="etapa-vinculo-valor">
                      <SelectValue placeholder="Selecione o valor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione...</SelectItem>
                      {getCampoVinculo(etapaForm.vinculo_automatico)?.valores.map(valor => (
                        <SelectItem key={valor} value={valor}>
                          {valor === "true" ? "Sim" : valor === "false" ? "Não" : valor}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setShowEtapaModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEtapa} data-testid="save-etapa-btn">
              {editingEtapa ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Modal - Para marcar etapas de uma pessoa */}
      <Dialog open={showProgressModal} onOpenChange={setShowProgressModal}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Footprints className="h-5 w-5 text-purple-600" />
              Trilho do Reino
            </DialogTitle>
            <DialogDescription>
              {selectedPessoa && (
                <span className="font-medium text-foreground">{selectedPessoa.nome_completo}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {loadingProgress ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            ) : (
              <>
                {/* Progress bar */}
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                    style={{ width: `${etapas.length > 0 ? (pessoaProgresso.length / etapas.length) * 100 : 0}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  {pessoaProgresso.length} de {etapas.length} etapas concluídas
                </p>

                {etapas.filter(e => e.ativo).map((etapa, index) => {
                  const completed = isEtapaCompleted(etapa.id);
                  const progress = getProgressRecord(etapa.id);
                  
                  return (
                    <div 
                      key={etapa.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                        completed ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                      } text-sm font-bold`}>
                        {completed ? <Check className="h-4 w-4" /> : index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm truncate ${completed ? 'text-green-700' : 'text-gray-700'}`}>
                          {etapa.nome}
                        </p>
                        {progress?.data_conclusao && (
                          <p className="text-xs text-green-600">
                            {new Date(progress.data_conclusao).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                      {completed ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleRemoveProgress(progress.id, e)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleAddProgress(etapa.id, e)}
                          className="text-green-600 border-green-300 hover:bg-green-50 shrink-0"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setShowProgressModal(false)} className="w-full">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
